import clsx from 'clsx';
import type * as React from 'react';

import {
    type FileUploadMetadata,
    getStorageCachedURL,
    isImageFile,
    isOpenAPIFile,
} from '@gitbook/app-database';
import type * as hiveCore from '@gitbook/hive-core';
import { Icon, type SVGIconProps } from '@gitbook/spine-icons';
import { Pressable } from '@gitbook/spine-react';

import { getImageResizedURL } from '../../utils';
import { useMediaGallery } from '../MediaGallery';

/**
 * Render an icon for a file.
 */
export function FileIcon(
    props: {
        file?: hiveCore.RevisionFile | FileUploadMetadata;
        size?: number;
        resizeWidth?: number;
        zoomable?: boolean;
    } & Pick<React.ComponentPropsWithoutRef<'div'>, 'className' | 'onMouseDown' | 'onPointerDown'>
): React.ReactElement {
    const { file, size, zoomable = false, resizeWidth, className, ...rest } = props;

    const mediaGallery = useMediaGallery();

    const isImage = file !== undefined && isImageFile(file);
    const imageZoomable = isImage && zoomable;

    const icon = (
        <div
            className={clsx(
                'relative flex items-center justify-center overflow-hidden rounded-sm bg-(--neutral-a3) text-(--neutral-9)',
                imageZoomable && 'cursor-zoom-in focus-visible:focus-ring',
                className
            )}
            style={{ width: size, height: size }}
            tabIndex={imageZoomable ? 0 : undefined}
            {...rest}
        >
            {file && isImage ? (
                <img
                    alt={file.name}
                    src={getImageResizedURL(file.downloadURL, {
                        width: resizeWidth ?? size,
                    })}
                    className="size-[calc(100%-0.25rem)] object-contain"
                    onDragStart={(event) => event.preventDefault()}
                />
            ) : (
                <SVGIconForFile file={file} className="size-1/2" />
            )}
        </div>
    );

    if (!imageZoomable) {
        return icon;
    }

    return (
        <Pressable
            onPress={() => {
                mediaGallery.open([
                    {
                        url: getStorageCachedURL(file.downloadURL),
                    },
                ]);
            }}
        >
            {icon}
        </Pressable>
    );
}

function SVGIconForFile(
    props: {
        file?: hiveCore.RevisionFile | FileUploadMetadata;
    } & SVGIconProps
) {
    const { file, ...rest } = props;

    if (!file) {
        return <Icon.Trashcan {...rest} />;
    }

    if (isOpenAPIFile(file)) {
        return <Icon.OpenAPI {...rest} />;
    }

    if (file.name.endsWith('.pdf')) {
        return <Icon.PDF {...rest} />;
    }

    return <Icon.Paperclip {...rest} />;
}
